/**
 * Settings feature (Preact): a page with application settings, including a
 * theme dropdown (Auto / Light / Dark) and web push notifications.
 */
import { useState, useEffect } from 'preact/hooks';
import { getTheme, setTheme } from '../lib/theme.js';
import { pushSupported, getSubscription, subscribe as subscribePush, unsubscribe as unsubscribePush } from '../lib/push.js';
import { getAuthStatus, login, logout } from '../lib/auth.js';

export function SettingsPage() {
  const [theme, setThemeLocal] = useState(() => getTheme());
  const [supported] = useState(() => typeof window !== 'undefined' && pushSupported());
  const [subState, setSubState] = useState('checking'); // checking | subscribed | unsubscribed
  const [error, setError] = useState('');
  const [auth, setAuth] = useState(null); // null | { oidc_enabled, authenticated, name, email, ... }

  useEffect(() => {
    let alive = true;
    if (!supported) {
      setSubState('unsubscribed');
      return;
    }
    getSubscription()
      .then((sub) => {
        if (!alive) return;
        setSubState(sub ? 'subscribed' : 'unsubscribed');
      })
      .catch(() => {
        if (alive) setSubState('unsubscribed');
      });
    return () => {
      alive = false;
    };
  }, [supported]);

  useEffect(() => {
    let alive = true;
    getAuthStatus()
      .then((s) => {
        if (alive) setAuth(s || null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const onChange = (e) => {
    const next = e.currentTarget.value;
    setTheme(next);
    setThemeLocal(next);
  };

  const onSubscribe = async () => {
    setError('');
    try {
      await subscribePush();
      setSubState('subscribed');
    } catch (err) {
      setError(err.message || 'Could not subscribe to notifications');
    }
  };

  const onUnsubscribe = async () => {
    setError('');
    try {
      await unsubscribePush();
      setSubState('unsubscribed');
    } catch (err) {
      setError(err.message || 'Could not unsubscribe from notifications');
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center gap-4 px-4 py-3 border-b border-base-300 bg-base-100">
        <h1 className="text-lg font-semibold text-base-content truncate">Settings</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="w-full max-w-3xl mx-auto flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-base-300 bg-base-100 p-4">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-base-content">Theme</div>
              <div className="text-xs text-base-content/50">
                Choose between light, dark, or follow your system.
              </div>
            </div>
            <select
              className="select select-bordered select-sm"
              value={theme}
              onChange={onChange}
            >
              <option value="auto">Auto</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>

          {auth && auth.oidc_enabled && (
            <div className="flex items-start justify-between gap-4 rounded-xl border border-base-300 bg-base-100 p-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-base-content">Account</div>
                {auth.authenticated ? (
                  <div className="flex flex-col gap-0.5 mt-1">
                    {auth.name && (
                      <span className="text-sm text-base-content truncate">{auth.name}</span>
                    )}
                    {auth.email && (
                      <span className="text-xs text-base-content/50 truncate">{auth.email}</span>
                    )}
                    {auth.subject && (
                      <span className="text-xs text-base-content/40 truncate font-mono">
                        ID: {auth.subject}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-base-content/50">
                    Signed out — sign in to use Talos.
                  </div>
                )}
              </div>
              {auth.authenticated ? (
                <button className="btn btn-outline btn-sm" onClick={logout}>
                  Sign out
                </button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={login}>
                  Sign in
                </button>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 rounded-xl border border-base-300 bg-base-100 p-4">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-base-content">Notifications</div>
              <div className="text-xs text-base-content/50">
                Get a push notification when a session you are not viewing
                receives a new response.
              </div>
              {error && <p className="text-error text-xs mt-1">{error}</p>}
            </div>
            {!supported ? (
              <span className="text-xs text-base-content/50">Not supported in this browser</span>
            ) : subState === 'checking' ? (
              <span className="loading loading-spinner loading-sm" />
            ) : subState === 'subscribed' ? (
              <button className="btn btn-outline btn-sm" onClick={onUnsubscribe}>
                Unsubscribe
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={onSubscribe}>
                Subscribe
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}